/**
 * assets/dm-core.js — the brand JavaScript core (PLAN §1.2 decision 7, §9.11; doc F §1).
 *
 * Exports, as script-scope globals — deliberately not an ES module, because
 * Dawn's `publish` / `subscribe` / `PUB_SUB_EVENTS` / `routes` / `fetchConfig`
 * are script-scope globals a module cannot see:
 *   window.DMElement     the base class every `dm-*` component extends
 *   window.DM_MOTION_MQ  the one shared prefers-reduced-motion MediaQueryList
 *   window.DM.lazy()     rescan for IntersectionObserver-gated section scripts
 *
 * WHY A CUSTOM-ELEMENT BASE AND NOT AN INIT FUNCTION
 * The theme editor and the Section Rendering API do not notify your code — they
 * *replace a section's DOM subtree*, then fire `shopify:section:load`. `<script>`
 * tags inside a rendering response are never re-executed, but custom elements in
 * that response are always upgraded. So anything initialised in an IIFE or on
 * DOMContentLoaded works on first paint and is inert the moment a merchant edits
 * a setting — and every `window`/`document` listener it bound leaks one copy per
 * edit, each holding a stale rect for a node that no longer exists.
 *
 * ONE AbortController PER MOUNT — the whole reason this class exists. Listeners
 * go through `this.on()` (bound to the mount's signal), Dawn pubsub through
 * `this.sub()` (`subscribe()` RETURNS the unsubscribe fn; not calling it is the
 * leak), observers through `this.track()`. Subtree replacement fires
 * `disconnectedCallback`, which releases all three; the replacement subtree
 * upgrades into a clean `connectedCallback`. The leaked-handler-per-edit bug
 * class is therefore structurally impossible, not merely discouraged, and no
 * component needs a `shopify:section:load` handler to achieve it.
 *
 * Classic script, ES2020, `defer`-safe, zero dependencies. Emitted once from
 * `layout/theme.liquid` after Dawn's constants.js / pubsub.js / global.js;
 * `defer` preserves document order, so it always runs before the per-section
 * `dm-*.js` files emitted in <body>.
 */
(function () {
  'use strict';

  // Double inclusion is a no-op — the same guard Dawn uses on its own elements.
  if (window.DMElement) return;

  /**
   * The shared reduced-motion query. One MediaQueryList for the whole page:
   * every animated component reads `this.motionOK` rather than re-querying,
   * and gets a `motionChanged()` callback when the user flips the OS setting
   * mid-session (a real case on macOS/iOS, and free to support).
   */
  const DM_MOTION_MQ = window.matchMedia('(prefers-reduced-motion: reduce)');

  class DMElement extends HTMLElement {
    connectedCallback() {
      // Re-entrancy: a DOM *move* (theme-editor reorder) fires disconnect then
      // connect. Tearing down an already-live mount keeps that idempotent.
      if (this._ac) this.disconnectedCallback();

      this._ac = new AbortController();
      this._unsubs = [];
      this._observers = [];
      this._rafPending = false;

      this.on(DM_MOTION_MQ, 'change', () => {
        if (this.motionChanged) this.motionChanged();
      });

      // `shopify:block:select` is one of the few editor events the architecture
      // does NOT give us for free: a block inside a closed <details> or a hidden
      // tab panel is invisible to the merchant who just clicked it in the
      // sidebar. Components opt in by implementing `revealBlock(el, detail)`.
      // Bound only in the editor, and torn down with everything else.
      if (this.editing && typeof this.revealBlock === 'function') {
        this.on(document, 'shopify:block:select', (event) => {
          if (this.contains(event.target)) this.revealBlock(event.target, event.detail);
        });
      }

      // A throwing component must not take the rest of the page down with it.
      try {
        if (this.init) this.init();
      } catch (error) {
        console.error(this.tagName.toLowerCase(), error);
      }
    }

    disconnectedCallback() {
      if (!this._ac) return; // never mounted, or already torn down
      this._ac.abort(); // every listener bound through this.on()
      this._unsubs.forEach((fn) => fn()); // every Dawn pubsub subscription
      this._observers.forEach((observer) => observer.disconnect()); // every IO/RO/MO
      this._unsubs.length = 0;
      this._observers.length = 0;
      this._ac = null;
      // Only for state that lives OUTSIDE this element (a body scroll lock, a
      // class on <html>). Everything inside the subtree dies with the subtree.
      if (this.destroy) this.destroy();
    }

    /** addEventListener that removes itself on disconnect. Never call the raw one. */
    on(target, type, handler, options) {
      if (!this._ac) return;
      target.addEventListener(type, handler, Object.assign({}, options, { signal: this._ac.signal }));
    }

    /** Dawn pubsub subscription that unsubscribes itself on disconnect. */
    sub(eventName, handler) {
      this._unsubs.push(subscribe(eventName, handler));
    }

    /** Register any observer (IntersectionObserver, ResizeObserver, MutationObserver)
     *  for automatic disconnect. Returns it, so it can be used inline. */
    track(observer) {
      this._observers.push(observer);
      return observer;
    }

    /** Coalesce N calls into one animation frame. All layout reads and all
     *  writes happen here — never inside a raw scroll or resize handler. */
    frame(fn) {
      if (this._rafPending) return;
      this._rafPending = true;
      requestAnimationFrame((t) => {
        this._rafPending = false;
        fn(t);
      });
    }

    /** Run `enter` while the element is near the viewport, `exit` when it leaves.
     *  The lazy-init gate for every scroll-coupled behaviour. */
    gate(enter, exit, rootMargin = '200px 0px') {
      this.track(
        new IntersectionObserver(([entry]) => (entry.isIntersecting ? enter() : exit()), { rootMargin })
      ).observe(this);
    }

    /** ResizeObserver over `resize`: it also catches font swap, image load and
     *  theme-editor content edits, which `resize` does not. */
    observeResize(fn) {
      this.track(new ResizeObserver(fn)).observe(this);
    }

    /** Write an attribute only when the value actually changes — the CSS↔JS
     *  interface is a fixed, small list (PLAN §9.11) and repeat writes on a
     *  scroll path are wasted style invalidations. Pass null to remove. */
    attr(node, name, value) {
      if (value === null || value === false) {
        if (node.hasAttribute(name)) node.removeAttribute(name);
      } else if (node.getAttribute(name) !== String(value)) {
        node.setAttribute(name, value);
      }
    }

    /* Configuration arrives from Liquid as data-*; there are no magic numbers
       in brand JS. `data-slide-gap="12"` → this.num('slideGap', 0). */
    num(name, fallback) {
      const value = parseFloat(this.dataset[name]);
      return Number.isFinite(value) ? value : fallback;
    }

    bool(name) {
      return this.dataset[name] === 'true';
    }

    str(name, fallback = '') {
      return this.dataset[name] || fallback;
    }

    get motionOK() {
      return !DM_MOTION_MQ.matches;
    }

    get scrollBehavior() {
      return this.motionOK ? 'smooth' : 'auto';
    }

    /** Soften behaviour in the editor (no auto-advance, no smooth scroll on
     *  init) — never disable a component: merchants must see what they edit. */
    get editing() {
      return !!(window.Shopify && window.Shopify.designMode);
    }

    /** Define-once helper. Makes double registration harmless when two sections
     *  on the same page both emit the component's script tag. */
    static define(tag, constructor) {
      if (!customElements.get(tag)) customElements.define(tag, constructor);
    }
  }

  /* ── Lazy script loader ───────────────────────────────────────────────────
     Two components (`dm-scrub`, `dm-picker`) are below the fold on every
     template they appear on, so their scripts are lazy-*loaded*, not merely
     lazy-initialised. Liquid emits the element with
     `data-dm-lazy-src="{{ 'dm-scrub.js' | asset_url }}"`; this observer injects
     the script once the element is within 400px of the viewport. The custom
     element upgrades on definition, so nothing else has to be re-run.
     No IntersectionObserver (or the merchant editing the section) → load now. */
  const injected = new Set();

  function inject(src) {
    if (!src || injected.has(src)) return;
    injected.add(src);
    const script = document.createElement('script');
    script.src = src;
    script.defer = true;
    document.head.appendChild(script);
  }

  let lazyObserver = null;

  function scanLazy() {
    const nodes = document.querySelectorAll('[data-dm-lazy-src]:not([data-dm-lazy-seen])');
    if (!nodes.length) return;
    const eager = !('IntersectionObserver' in window) || !!(window.Shopify && window.Shopify.designMode);
    if (!eager && !lazyObserver) {
      lazyObserver = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            observer.unobserve(entry.target);
            inject(entry.target.dataset.dmLazySrc);
          });
        },
        { rootMargin: '400px 0px' }
      );
    }
    nodes.forEach((node) => {
      node.dataset.dmLazySeen = 'true';
      if (eager) inject(node.dataset.dmLazySrc);
      else lazyObserver.observe(node);
    });
  }

  // The one place the section lifecycle needs a document-level listener: a
  // section re-rendered by the editor may contain a lazy placeholder that has
  // never been observed. (`shopify:section:unload` needs no handler — removing
  // the subtree disconnects its elements, and the observer drops the target.)
  document.addEventListener('shopify:section:load', scanLazy);

  window.DMElement = DMElement;
  window.DM_MOTION_MQ = DM_MOTION_MQ;
  window.DM = { lazy: scanLazy };

  // `defer` guarantees the document is parsed before this runs, so the first
  // scan is safe to do immediately.
  scanLazy();
})();
